import type { SendVerdictTally } from '@/model/Agent.model';
import type { FC, ReactNode } from 'react';
import { passedInPartOf } from '@/components/Agents/SendValidator/utils/SendRuleOutcomes';
import { formatCount } from '@/components/Agents/SendValidator/utils/SendText';
import styles from './SendResults.module.scss';

/** One row of a tally table: what it counts, and the server's tally for it. */
export interface SendTallyRow {
  key: string;
  label: ReactNode;
  tally: SendVerdictTally;
  /** The row's `data-test-element`, so a tier's own row and its by-source rows stay apart. */
  element: string;
  className?: string;
}

interface SendTallyTableProps {
  caption: ReactNode;
  /** What the first column names: a tier, a dataset. */
  heading: string;
  rows: ReadonlyArray<SendTallyRow>;
  /** Prefixes the passed and total cells' `data-test-element`. */
  cellPrefix: string;
  testId?: string;
}

/**
 * How the published rules landed, one row per tier or dataset, as the server counted them: passed (with how
 * many only in part, as the tally tiles say, so a pass short of an exact, finished check never reads as one),
 * failed, not applicable, not evaluated and the row's total. Every cell is the server's number.
 */
const SendTallyTable: FC<SendTallyTableProps> = ({
  caption,
  heading,
  rows,
  cellPrefix,
  testId = 'invivo-send-validator__tally-table',
}) => (
  <table className={styles.grid} data-test-component="SendTallyTable" data-testid={testId}>
    <caption className={styles.gridCaption} data-test-element={`${cellPrefix}-grid-caption`}>
      {caption}
    </caption>
    <thead>
      <tr>
        <th scope="col">{heading}</th>
        <th scope="col">Passed</th>
        <th scope="col">Failed</th>
        <th scope="col">Not applicable</th>
        <th scope="col">Not evaluated</th>
        <th scope="col">Rules</th>
      </tr>
    </thead>
    <tbody>
      {rows.map(({ key, label, tally, element, className }) => {
        const inPart = passedInPartOf(tally);
        return (
          <tr key={key} className={className} data-test-element={element} data-test-key={key}>
            <th scope="row">{label}</th>
            <td data-test-element={`${cellPrefix}-passed`}>
              {formatCount(tally.passed)}
              {inPart > 0 && <span className={styles.cellNote}>{formatCount(inPart)} in part</span>}
            </td>
            <td>{formatCount(tally.failed)}</td>
            <td>{formatCount(tally.not_applicable)}</td>
            <td>{formatCount(tally.not_evaluated)}</td>
            <td data-test-element={`${cellPrefix}-total`}>{formatCount(tally.total)}</td>
          </tr>
        );
      })}
    </tbody>
  </table>
);

export default SendTallyTable;
